import { useEffect, useRef, useState } from "react"
import { useIntl, FormattedMessage } from "react-intl"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck } from "@fortawesome/free-solid-svg-icons"

import RequirementItem from "./RequirementItem"
import SkillAutocomplete from "../../../common/SkillAutocomplete"
import "../../../common/SkillAutocomplete/SkillAutocomplete.css"

// const sections = ["soft skills", "hard skills", "experience", "education"]
const sections = ["hard_skills", "soft_skills"]

const sectionTitles = {
  "hard_skills": "common.hardSkills",
  "soft_skills": "common.softSkills",
  // experience: "common.experience",
  // education: "common.education",
}

const Requirements = ({ data, setData, disableActions = false }) => {
  const [editable, setEditable] = useState("")

  const { job_requirements } = data

  useEffect(() => {
    const el = document.getElementById(editable)

    el?.focus()
  }, [editable])

  return (
    <>
      {data?.job_requirements?.length > 0 &&
        <div className="requirement__highlights-wrapper">
          <div className="requirement__highlights-item">
            <p className="requirement__highlights-title">
              <FormattedMessage id="common.highlights" />
            </p>
          </div>

          {job_requirements?.map((value) => (
            <div className="requirement__highlights-item" key={`Highlight - ${value}`}>
              <p className="requirement__highlights-info">{value}</p>
            </div>
          ))}
        </div>
      }

      {sections.map((section, index) => {
        return (
          <Section
            key={section}
            data={data}
            section={section}
            editable={editable}
            setEditable={setEditable}
            setData={setData}
            disableActions={disableActions}
          />
        )
      })}
    </>
  );
}

const Section = ({ data, section, editable, setEditable, setData, disableActions = false }) => {
  const [skillObject, setSkillObject] = useState({ skill: "" })
  const [isSkillSelected, setIsSkillSelected] = useState(false)
  const [validationMessage, setValidationMessage] = useState("")

  const autocompleteRef = useRef()
  const { formatMessage } = useIntl()

  const add = () => {
    if (skillObject.skill.trim().length === 0) return;
    
    // Only add skill if it was selected from autocomplete
    if (!isSkillSelected) {
      setValidationMessage("Please select a skill from the suggestions");
      setTimeout(() => setValidationMessage(""), 3000); // Clear message after 3 seconds
      return;
    }
    
    setIsSkillSelected(false)
    setValidationMessage("")
    setData((prevData) => ({
      ...prevData,
      [section]: [
        ...prevData[section],
        {
          relationship: "optional",
          ...skillObject,
          edited: true,
          explanation: "" // TODO: implement the option to add the explanation to the newly added skill
        },
      ]
    }));
    setSkillObject({ skill: "" })
  }

  const handleSkillSelect = (skill) => {
    setSkillObject(skill);
    setIsSkillSelected(true); // Mark as selected from autocomplete
    setValidationMessage(""); // Clear any validation message
  }

  const handleInputChange = (skill) => {
    setSkillObject(skill);
    setIsSkillSelected(false); // Reset selection flag when user types
    setValidationMessage(""); // Clear validation message when typing
  }

  const focusInput = () => {
    // Focus the input inside the autocomplete component
    const input = autocompleteRef.current?.querySelector('input');
    input?.focus();
  }

  const addNewSkillAction = (
    <div className="req-add">
      <div ref={autocompleteRef}>
        <SkillAutocomplete
          value={skillObject.skill}
          onChange={handleInputChange}
          onSelect={handleSkillSelect}
          placeholder={formatMessage({ id: "common.add" }) + " (select from suggestions)"}
          className="req-add__autocomplete"
          onKeyDown={(e) => e.key === "Enter" && add()}
          isSelected={isSkillSelected}
          type={section.replace("_", "-").replace("skills", "skill")}
        />
        {validationMessage && (
          <div className="validation-message">
            {validationMessage}
          </div>
        )}
      </div>

      {skillObject.skill?.length > 2 && isSkillSelected ? (
        <button className="btn btn--icon req-add__icon" onMouseDown={add}>
          <FontAwesomeIcon icon={faCheck} />
        </button>
      ) : (
        <button
          className="btn btn--icon req-add__icon"
          onClick={focusInput}
        >
          {addSvg}
        </button>
      )}
    </div>
  );

  const sectionData = data[section];

  return (
    <div className="box">
      {sectionData && (
        <h4 className="h4">
          <FormattedMessage id={sectionTitles[section]} />
        </h4>
      )}

      <div className="box__requirements">
        {sectionData?.map((item, i) => {
          const id = `${section}-${i}`

          return (
            <RequirementItem
              key={id}
              section={section}
              index={i}
              item={item}
              disabled={disableActions}
              editable={editable}
              setEditable={setEditable}
              deletable={!disableActions}
              setData={setData}
            />
          )
        })}

        {!disableActions && addNewSkillAction}
      </div>
    </div>
  )
}

const addSvg = (
  <svg
    width="12"
    height="13"
    viewBox="0 0 12 13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8.52829 1.882C8.66179 1.74851 8.82027 1.64261 8.99468 1.57037C9.1691 1.49812 9.35604 1.46094 9.54483 1.46094C9.73362 1.46094 9.92055 1.49812 10.095 1.57037C10.2694 1.64261 10.4279 1.74851 10.5614 1.882C10.6949 2.01549 10.8007 2.17397 10.873 2.34839C10.9452 2.52281 10.9824 2.70974 10.9824 2.89853C10.9824 3.08732 10.9452 3.27426 10.873 3.44868C10.8007 3.62309 10.6949 3.78157 10.5614 3.91507L3.69976 10.7767L0.904297 11.5391L1.6667 8.7436L8.52829 1.882Z"
      stroke="#5EC58F"
      strokeWidth="1.01653"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default Requirements
